import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export type TagChipState = 'default' | 'subtle' | 'hover' | 'pressed';

/**
 * Typography weight for the inner label. Defaults to `'medium'` per the master
 * Tag (Figma `289:50564`, used by FilterChipRow). `'regular'` matches the
 * 13px / weight-400 / 1.3-line-height variant rendered by FilterPillGroup tab
 * pills (Figma `984:7637`) and BreadcrumbTagRow chips (Figma `5:23036` subtle
 * resting variant) — both call sites pass `weight="regular"` explicitly.
 *
 * `weight` is only consulted for `size='md'`. The `sm` size is fixed at 10px
 * regular (Figma `5:23464`) so the prop is ignored there.
 */
export type TagChipWeight = 'medium' | 'regular';

/**
 * Size variants:
 * - `md` (default) — master Tag per Figma `289:50564`: `h-[26px]`, `px-2`,
 *   `rounded-[4px]`, inner label at `text-p1` or `text-p1-med` driven by
 *   `weight`. Used by FilterPillGroup, BreadcrumbTagRow, FilterChipRow.
 * - `sm` — small chip per Figma `5:23464`: auto height, `px-1 py-0.5`
 *   (4×2 px), `rounded-[2px]`, inner label fixed at `text-[10px]
 *   font-normal leading-[1.2] capitalize`. Used for the Top Data Accessed
 *   widget's Document Topic column.
 */
export type TagChipSize = 'md' | 'sm';

export interface TagChipProps {
  children: ReactNode;
  /**
   * Visual state per Figma `289:50564`/`289:50571` (master Tag) and
   * `5:23036` (subtle resting variant used by BreadcrumbTagRow):
   * - `default` — white (master Default).
   * - `subtle` — grey-100, used as the resting visual for breadcrumb chips
   *   and the Top Data Accessed Document Topic chip (Figma `5:23464`).
   * - `hover` — grey-100, master Hover treatment over `default`.
   * - `pressed` — dawn-orange (#FF5722) with white text (selected).
   */
  state?: TagChipState;
  /**
   * Layout + typography variant. Defaults to `'md'` for back-compat with all
   * existing callers. See `TagChipSize` for spec details.
   */
  size?: TagChipSize;
  /**
   * Inner label typography weight (md only). Defaults to `'medium'`
   * (`text-p1-med`) per the master Tag (Figma `289:50564`). Set
   * `'regular'` for FilterPillGroup tab pills (Figma `984:7637`) and
   * BreadcrumbTagRow chips (Figma `5:23036` subtle resting variant).
   * Ignored when `size='sm'`.
   */
  weight?: TagChipWeight;
  /**
   * Optional 12×12 trailing glyph (typically a chevron). Sized to `w-3 h-3`.
   */
  trailingIcon?: ReactNode;
  className?: string;
}

const STATE_STYLES = {
  default: 'bg-white text-dawn-black',
  subtle: 'bg-grey-100 text-dawn-black',
  hover: 'bg-grey-100 text-dawn-black',
  // Pressed bg is `dawn-orange` (#FF5722) per Figma `5:23321`, NOT `orange-500`
  // (#FF8533). v1 used the orange-500 ramp step; the master Tag pressed frame
  // uses Dawn's brand orange. See feedback_standards_outrank_design.
  pressed: 'bg-dawn-orange text-dawn-white',
} as const satisfies Readonly<Record<TagChipState, string>>;

/**
 * Layout per size variant. `md` mirrors the master Tag (Figma `289:50564`);
 * `sm` mirrors Figma `5:23464` (`px-[4px] py-[2px] rounded-[2px]`, height
 * auto so the chip hugs its 10px label).
 */
const SIZE_LAYOUT_STYLES = {
  md: 'h-[26px] px-2 rounded-[4px]',
  sm: 'px-1 py-0.5 rounded-[2px]',
} as const satisfies Readonly<Record<TagChipSize, string>>;

/**
 * Inner-label typography per md `weight`. Custom font-size tokens
 * (`text-p1`, `text-p1-med`) collide with `text-{color}` utilities under
 * tailwind-merge defaults, so the typography lives on a child span isolated
 * from the parent's color utilities. See `feedback_packages_for_clean_utils`
 * and `Common Gotchas` in patterns.md.
 *
 * `sm` size sidesteps the collision by using a bracketed font-size literal
 * (`text-[10px]`) — see SensitivityChip pattern.
 */
const WEIGHT_STYLES = {
  medium: 'text-p1-med',
  regular: 'text-p1',
} as const satisfies Readonly<Record<TagChipWeight, string>>;

const SM_TYPOGRAPHY = 'text-[10px] font-normal leading-[1.2] capitalize';

export function TagChip({
  children,
  state = 'default',
  size = 'md',
  weight = 'medium',
  trailingIcon,
  className,
}: TagChipProps) {
  const labelClass = size === 'sm' ? SM_TYPOGRAPHY : WEIGHT_STYLES[weight];

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center gap-1',
        SIZE_LAYOUT_STYLES[size],
        STATE_STYLES[state],
        className
      )}
    >
      <span className={labelClass}>{children}</span>
      {trailingIcon ? (
        <span className="inline-flex h-3 w-3 items-center justify-center">{trailingIcon}</span>
      ) : null}
    </div>
  );
}

TagChip.displayName = 'TagChip';
